import { Popover, PopoverContent, PopoverTrigger } from '@crowdfpl/ui-web/popover';
import { Sheet, SheetContent } from '@crowdfpl/ui-web/sheet';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNotificationNavigationTarget } from '@/helpers/notification-navigation';
import { useAuth } from '@/hooks/auth';
import {
  useMarkAllAsRead,
  useMarkAsRead,
  useNotificationPreferences,
  useNotifications,
  useUnreadCount,
  useUpdateNotificationPreferences,
} from '@/hooks/data/notifications';
import { useIsMobile } from '@/hooks/utils/use-mobile';
import { useAuthModal, useNotificationPreferencesModal } from '@/stores/ui';
import type { NotificationFilter, NotificationItemDto } from '@/types/notifications';
import { NotificationBell } from './notification-bell';
import { NotificationEmptyState } from './notification-empty-state';
import { NotificationHeader } from './notification-header';
import { NotificationItem } from './notification-item';
import { NotificationSkeleton } from './notification-skeleton';

export function NotificationCenter() {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuth((s) => s.isAuthenticated);
  const { showAuthModal } = useAuthModal();
  const { showNotificationPreferencesModal } = useNotificationPreferencesModal();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<NotificationFilter>('all');
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const { data: unreadData } = useUnreadCount();
  const unreadCount = unreadData?.count ?? 0;

  const { data: prefsData } = useNotificationPreferences();
  const { mutate: markPanelSeen } = useUpdateNotificationPreferences();

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (nextOpen && prefsData && !prefsData.hasSeenNotificationPanel) {
        markPanelSeen({ hasSeenNotificationPanel: true });
      }
    },
    [prefsData, markPanelSeen],
  );

  useEffect(() => {
    if (!open) return;
    void queryClient.invalidateQueries({ queryKey: ['notifications', 'feed'] });
  }, [open, queryClient]);

  const {
    data: feedData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useNotifications(filter, open);

  const markAsRead = useMarkAsRead();
  const markAll = useMarkAllAsRead();

  const items = useMemo(
    () => feedData?.pages.flatMap((p) => p.notifications) ?? [],
    [feedData],
  );

  const handleActivate = useCallback(
    (n: NotificationItemDto) => {
      const target = getNotificationNavigationTarget(n.action_url);
      if (target) {
        navigate(target);
      }
      markAsRead.mutate(n.id);
      setOpen(false);
    },
    [markAsRead, navigate],
  );

  const onGuestBellClick = useCallback(() => {
    showAuthModal('Sign in to view your notifications');
  }, [showAuthModal]);

  const handleOpenNotificationSettings = useCallback(() => {
    setOpen(false);
    showNotificationPreferencesModal();
  }, [showNotificationPreferencesModal]);

  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!open || !root || !sentinel) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { root, rootMargin: '80px' },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [
    open,
    filter,
    items.length,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  if (!isAuthenticated) {
    return <NotificationBell panelOpen={false} onClick={onGuestBellClick} />;
  }

  const feedBody = (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <NotificationHeader
        filter={filter}
        onFilterChange={setFilter}
        unreadCount={unreadCount}
        onMarkAllAsRead={() => markAll.mutate()}
        isMarkingAll={markAll.isPending}
        onOpenSettings={handleOpenNotificationSettings}
      />
      <div
        ref={scrollRef}
        role="feed"
        aria-label="Notifications"
        className="flex min-h-0 flex-1 flex-col overflow-y-auto"
      >
        {isLoading ? (
          <div className="space-y-1 py-1">
            {Array.from({ length: 4 }).map((_, i) => (
              <NotificationSkeleton key={i} />
            ))}
          </div>
        ) : items.length === 0 ? (
          <NotificationEmptyState variant={filter === 'unread' ? 'all-read' : 'empty'} />
        ) : (
          <div className="flex flex-col">
            {items.map((n) => (
              <NotificationItem key={n.id} notification={n} onActivate={handleActivate} />
            ))}
          </div>
        )}
        {isFetchingNextPage ? (
          <div className="space-y-1 py-1">
            {Array.from({ length: 2 }).map((_, i) => (
              <NotificationSkeleton key={`more-${i}`} />
            ))}
          </div>
        ) : null}
        {!isLoading && items.length > 0 && !hasNextPage && filter === 'all' ? (
          <NotificationEmptyState variant="end-of-feed" />
        ) : null}
        <div ref={sentinelRef} className="h-4 w-full shrink-0" aria-hidden />
      </div>
    </div>
  );

  const liveRegion = (
    <span className="sr-only" aria-live="polite" aria-atomic>
      {unreadCount === 0
        ? 'No unread notifications'
        : `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}`}
    </span>
  );

  if (isMobile) {
    return (
      <>
        {liveRegion}
        <NotificationBell panelOpen={open} onClick={() => handleOpenChange(!open)} />
        <Sheet open={open} onOpenChange={handleOpenChange}>
          <SheetContent
            side="bottom"
            className="flex h-[70vh] max-h-[70vh] flex-col gap-0 overflow-hidden p-0 pb-[env(safe-area-inset-bottom)] sm:max-w-full"
          >
            {feedBody}
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      {liveRegion}
      <PopoverTrigger asChild>
        <NotificationBell panelOpen={open} />
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        className="z-[100] flex !max-h-[480px] w-[400px] flex-col overflow-hidden border-border bg-background p-0 text-foreground shadow-lg"
      >
        {feedBody}
      </PopoverContent>
    </Popover>
  );
}
